<template>
  <!-- Desktop: unified horizontal strip -->
  <div class="hidden sm:flex bg-white dark:bg-[#1C2128] rounded-xl border border-gray-100 dark:border-gray-700 divide-x divide-gray-100 dark:divide-gray-700 overflow-hidden">
    <div v-for="card in cards" :key="card.name" class="flex-1 px-5 py-4">

      <!-- Skeleton -->
      <div v-if="loading && !card.data" class="animate-pulse space-y-2">
        <div class="flex items-center gap-2">
          <div class="w-2 h-2 rounded-full bg-gray-200 dark:bg-gray-700"></div>
          <div class="h-2.5 bg-gray-200 dark:bg-gray-700 rounded w-20"></div>
        </div>
        <div class="h-7 bg-gray-100 dark:bg-gray-800 rounded w-16 mt-1"></div>
        <div class="h-2.5 bg-gray-100 dark:bg-gray-800 rounded w-28"></div>
      </div>

      <!-- Loaded -->
      <template v-else>
        <div class="flex items-center gap-2 mb-1">
          <span class="w-2 h-2 rounded-full flex-shrink-0" :class="card.dot"></span>
          <span class="text-xs font-medium text-gray-500 dark:text-gray-400">{{ card.label }}</span>
        </div>
        <p class="text-2xl font-bold text-gray-900 dark:text-white tabular-nums leading-tight">
          {{ card.data?.count ?? '—' }}
        </p>
        <div class="flex items-center gap-1.5 mt-1.5 flex-wrap">
          <template v-if="card.data?.difference_with_previous_tenure">
            <span class="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded text-xs font-semibold"
              :class="trendBadge(card.data.difference_with_previous_tenure)">
              {{ trendArrow(card.data.difference_with_previous_tenure) }}
              {{ trendPct(card.data.difference_with_previous_tenure) }}
            </span>
            <span class="text-xs text-gray-400 dark:text-gray-500">
              {{ trendAbs(card.data.difference_with_previous_tenure) }} vs prev
            </span>
          </template>
          <span v-else class="text-xs text-gray-400 dark:text-gray-500">No prior data</span>
        </div>
      </template>
    </div>
  </div>

  <!-- Mobile: 2×2 grid -->
  <div class="sm:hidden grid grid-cols-2 gap-3">
    <div v-for="card in cards" :key="card.name + '_m'"
      class="bg-white dark:bg-[#1C2128] rounded-xl border border-gray-100 dark:border-gray-700 px-4 py-3.5">

      <div v-if="loading && !card.data" class="animate-pulse space-y-2">
        <div class="h-2.5 bg-gray-200 dark:bg-gray-700 rounded w-16"></div>
        <div class="h-6 bg-gray-100 dark:bg-gray-800 rounded w-12 mt-1"></div>
        <div class="h-2.5 bg-gray-100 dark:bg-gray-800 rounded w-20"></div>
      </div>

      <template v-else>
        <div class="flex items-center gap-1.5 mb-1">
          <span class="w-1.5 h-1.5 rounded-full flex-shrink-0" :class="card.dot"></span>
          <span class="text-[11px] font-medium text-gray-500 dark:text-gray-400">{{ card.label }}</span>
        </div>
        <p class="text-xl font-bold text-gray-900 dark:text-white tabular-nums leading-tight">
          {{ card.data?.count ?? '—' }}
        </p>
        <div class="flex items-center gap-1 mt-1 flex-wrap">
          <template v-if="card.data?.difference_with_previous_tenure">
            <span class="inline-flex items-center gap-0.5 px-1 py-0.5 rounded text-[10px] font-semibold"
              :class="trendBadge(card.data.difference_with_previous_tenure)">
              {{ trendArrow(card.data.difference_with_previous_tenure) }}
              {{ trendPct(card.data.difference_with_previous_tenure) }}
            </span>
            <span class="text-[10px] text-gray-400 dark:text-gray-500">
              {{ trendAbs(card.data.difference_with_previous_tenure) }}
            </span>
          </template>
          <span v-else class="text-[10px] text-gray-400 dark:text-gray-500">No prior data</span>
        </div>
      </template>
    </div>
  </div>
</template>

<script setup>
import { computed } from 'vue'
import { useAnalyticsTrafficStore } from 'stores/analyticsTraffic.js'

const props = defineProps({ duration: { type: String, required: true } })
const store = useAnalyticsTrafficStore()

const loading = computed(() => store.statsLoading)
const stats   = computed(() => store.stats[props.duration] || {})

const CARDS = [
  { name: 'total_users',     label: 'Visitors',     dot: 'bg-blue-500'    },
  { name: 'new_users',       label: 'New Visitors', dot: 'bg-emerald-500' },
  { name: 'recurring_users', label: 'Recurring',    dot: 'bg-violet-500'  },
  { name: 'converted_users', label: 'Converted',    dot: 'bg-orange-500'  },
]

const cards = computed(() => CARDS.map(c => ({ ...c, data: stats.value[c.name] })))

function trendPct(diff)   { const m = diff?.match(/\(([^)]+)\)/); return m ? m[1] : diff }
function trendAbs(diff)   { const m = diff?.match(/^([^(]+)/); return m ? m[1].trim() : '' }
function trendArrow(diff) { return diff?.startsWith('+') ? '↑' : diff?.startsWith('-') ? '↓' : '→' }
function trendBadge(diff) {
  if (diff?.startsWith('+')) return 'bg-emerald-50 dark:bg-emerald-900/20 text-emerald-600 dark:text-emerald-400'
  if (diff?.startsWith('-')) return 'bg-red-50 dark:bg-red-900/20 text-red-500 dark:text-red-400'
  return 'bg-gray-100 dark:bg-gray-800 text-gray-500 dark:text-gray-400'
}
</script>
